// Tool Usage panel for the thread watch page — what tools this thread calls,
// how long they take, and what parameters they're called with. Built to
// surface worst-case offenders: rows sort by slowest average (default),
// slowest max, or most called, and each row carries a duration bar scaled to
// the slowest tool (solid = average, ghost = max) so outliers jump out.
// Each tool expands into a per-parameter breakdown (numeric ranges + most
// common values).
import { useClient } from '@cortex/react-query';
import type { PersistentAgentToolStats, PersistentAgentToolUsage } from '@cortex/types';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { formatDuration, formatRelative } from '../../lib/format';

type SortKey = 'avg' | 'max' | 'count';

const sortLabels: Record<SortKey, string> = {
  avg: 'Slowest avg',
  max: 'Slowest max',
  count: 'Most called',
};

export function ToolUsagePanel({
  agentId,
  threadId,
  // When true the tool list isn't self-capped — the container (e.g. a tab panel)
  // owns the scroll, so you don't get a scrollbar nested inside a scrollbar.
  fillHeight = false,
}: {
  agentId: string;
  threadId: string;
  fillHeight?: boolean;
}) {
  const client = useClient();
  const [sort, setSort] = useState<SortKey>('avg');
  const usage = useQuery({
    queryKey: ['persistent-agent-tool-usage', threadId],
    queryFn: () =>
      client.http
        .get(`v1/persistent-agent-groups/${agentId}/agents/${threadId}/tools`)
        .json<PersistentAgentToolUsage>(),
    refetchInterval: 15_000,
  });

  if (usage.isError) {
    return (
      <div className="alert alert-danger">
        {(usage.error as Error)?.message ?? 'Failed to load tool usage'}
      </div>
    );
  }
  if (!usage.data) {
    return <span className="t-muted t-sm">Loading tool usage…</span>;
  }
  if (usage.data.tools.length === 0) {
    return <span className="t-muted t-sm">No tool calls yet.</span>;
  }

  const { totalCalls, totalErrors, sampledCalls, tools } = usage.data;
  const sorted = [...tools].sort((a, b) =>
    sort === 'count'
      ? b.count - a.count
      : (b.durationMs?.[sort] ?? -1) - (a.durationMs?.[sort] ?? -1),
  );
  // Bars are scaled against the slowest observed call across ALL tools, so a
  // short solid bar under a long ghost bar reads as "usually fast, spiked once".
  const scaleMs = Math.max(...tools.map((t) => t.durationMs?.max ?? 0), 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span className="t-xs t-muted">
          {totalCalls} call{totalCalls === 1 ? '' : 's'}
          {totalErrors > 0 ? ` · ${totalErrors} error${totalErrors === 1 ? '' : 's'}` : ''}
          {sampledCalls < totalCalls ? ` · stats over the last ${sampledCalls}` : ''}
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
          {(Object.keys(sortLabels) as SortKey[]).map((key) => (
            <button
              key={key}
              className={`btn btn--sm ${sort === key ? 'btn--primary' : 'btn--ghost'}`}
              onClick={() => setSort(key)}
              title={
                key === 'count'
                  ? 'Order by how often each tool is called'
                  : `Order by ${key === 'avg' ? 'average' : 'worst-case'} run time — slowest first`
              }
            >
              {sortLabels[key]}
            </button>
          ))}
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          ...(fillHeight ? {} : { maxHeight: 360, overflow: 'auto' }),
        }}
      >
        {sorted.map((tool) => (
          <ToolRow key={tool.name} tool={tool} scaleMs={scaleMs} />
        ))}
      </div>
    </div>
  );
}

function ToolRow({ tool, scaleMs }: { tool: PersistentAgentToolStats; scaleMs: number }) {
  const [expanded, setExpanded] = useState(false);
  const d = tool.durationMs;

  return (
    <div
      style={{
        background: 'rgba(232,171,127,0.06)',
        border: '1px solid rgba(232,171,127,0.15)',
        borderRadius: 'var(--radius-sm, 3px)',
        padding: '8px 12px',
      }}
    >
      <div
        onClick={() => setExpanded((v) => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontFamily: 'var(--font-mono)',
          fontSize: 12,
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <span style={{ fontSize: 9 }}>{expanded ? '▼' : '▶'}</span>
        <span style={{ color: 'var(--terminal-amber)', fontWeight: 500 }}>{tool.name}</span>
        <span className="badge badge--neutral" style={{ fontSize: 9 }}>
          ×{tool.count}
        </span>
        {tool.errorCount > 0 && (
          <span className="badge badge--danger" style={{ fontSize: 9 }}>
            {tool.errorCount} error{tool.errorCount === 1 ? '' : 's'}
          </span>
        )}
        <span className="t-xs" style={{ marginLeft: 'auto', color: 'var(--text-secondary)' }}>
          {d ? (
            <>
              avg {formatDuration(d.avg)} ·{' '}
              <span title="Worst case observed for this tool">max {formatDuration(d.max)}</span>
            </>
          ) : (
            'no timings yet'
          )}
          {tool.lastUsedAt ? (
            <span className="t-muted"> · used {formatRelative(tool.lastUsedAt)}</span>
          ) : null}
        </span>
      </div>
      {d && (
        <div
          title={`avg ${formatDuration(d.avg)} · min ${formatDuration(d.min)} · max ${formatDuration(d.max)}`}
          style={{
            position: 'relative',
            height: 4,
            background: 'rgba(255,255,255,0.06)',
            borderRadius: 2,
            marginTop: 7,
          }}
        >
          {/* Ghost bar: worst case. Solid bar: average. */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              width: `${Math.max((d.max / scaleMs) * 100, 1)}%`,
              background: 'rgba(232,171,127,0.28)',
              borderRadius: 2,
            }}
          />
          <div
            style={{
              position: 'absolute',
              inset: 0,
              width: `${Math.max((d.avg / scaleMs) * 100, 1)}%`,
              background: 'var(--terminal-amber)',
              borderRadius: 2,
            }}
          />
        </div>
      )}
      {expanded && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {d && (
            <div className="t-xs t-muted" style={{ fontFamily: 'var(--font-mono)' }}>
              min {formatDuration(d.min)} · avg {formatDuration(d.avg)} · max{' '}
              {formatDuration(d.max)}
            </div>
          )}
          {tool.parameters.length === 0 ? (
            <div className="t-xs t-muted">Called without parameters.</div>
          ) : (
            tool.parameters.map((p) => (
              <div
                key={p.key}
                style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}
              >
                <span
                  className="t-mono t-xs"
                  style={{ color: 'var(--text-secondary)', minWidth: 90 }}
                >
                  {p.key}
                </span>
                <span className="t-xs t-muted">×{p.count}</span>
                {p.numeric && (
                  <span className="t-xs" style={{ fontFamily: 'var(--font-mono)' }}>
                    {p.numeric.min === p.numeric.max
                      ? `= ${p.numeric.min}`
                      : `${p.numeric.min} – ${p.numeric.max} (avg ${p.numeric.avg})`}
                  </span>
                )}
                {p.topValues.map((v) => (
                  <span
                    key={v.value}
                    className="badge badge--outline t-mono"
                    style={{
                      fontSize: 9,
                      maxWidth: 260,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    title={v.value}
                  >
                    {v.value || '(empty)'}
                    {v.count > 1 ? ` ×${v.count}` : ''}
                  </span>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
